import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { RefreshCw, Settings, Loader2, CheckCircle, BarChart3, TrendingUp, Camera, Mail, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [clearLoading, setClearLoading] = useState(false);
  const [humorLevel, setHumorLevel] = useState('medium');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [trendingTopics, setTrendingTopics] = useState([]);
  
  // Background refresh state
  const [bgRefreshing, setBgRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchTrendingTopics();
  }, []);

  const fetchTrendingTopics = async () => {
    try {
      const response = await axios.get(`${API}/admin/trending-topics`, {
        headers: getAuthHeader()
      });
      setTrendingTopics(response.data.trending_topics || []);
    } catch (error) {
      console.error('Error fetching trending topics:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: getAuthHeader()
      });
      setHumorLevel(response.data.humor_level);
      setLastRefresh(response.data.last_refresh);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await axios.post(`${API}/admin/refresh-news`, {}, {
        headers: getAuthHeader()
      });
      toast.success(response.data.message);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refresh news');
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefreshByTrends = async () => {
    setTrendingLoading(true);
    try {
      const response = await axios.post(`${API}/admin/refresh-news-by-trends`, {}, {
        headers: getAuthHeader()
      });
      toast.success(response.data.message + ' from trending topics!');
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refresh trending news');
    } finally {
      setTrendingLoading(false);
    }
  };

  const handleGeneratePhoto = async () => {
    setPhotoLoading(true);
    try {
      const response = await axios.post(`${API}/admin/photo-of-the-week`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Photo of the Week created!');
      console.log('Photo created:', response.data);
    } catch (error) {
      toast.error('Failed to generate Photo of the Week');
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleSendNewsletter = async () => {
    setNewsletterLoading(true);
    try {
      const response = await axios.post(`${API}/admin/send-newsletter`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`Newsletter sent to ${response.data.sent} subscribers!`);
    } catch (error) {
      toast.error('Failed to send newsletter');
    } finally {
      setNewsletterLoading(false);
    }
  };

  const handleClearAllNews = async () => {
    if (!window.confirm('Delete ALL news articles? This cannot be undone.')) return;
    setClearLoading(true);
    try {
      const response = await axios.delete(`${API}/admin/clear-all-news`, {
        headers: getAuthHeader()
      });
      toast.success(`Deleted ${response.data.deleted} articles. Now refresh news!`);
    } catch (error) {
      toast.error('Failed to clear news');
    } finally {
      setClearLoading(false);
    }
  };

  const handleHumorLevelChange = async (level) => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/humor-level`, { humor_level: level }, {
        headers: getAuthHeader()
      });
      setHumorLevel(level);
      toast.success('Humor level updated');
    } catch (error) {
      toast.error('Failed to update humor level');
    } finally {
      setLoading(false);
    }
  };

  // Background refresh handlers
  const handleRefreshAllBackground = async () => {
    setBgRefreshing(true);
    try {
      const response = await axios.post(`${API}/admin/refresh-all-background`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Background refresh started! Check status below.');
      // Start polling for status
      pollRefreshStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start background refresh');
      setBgRefreshing(false);
    }
  };

  const pollRefreshStatus = async () => {
    const poll = async () => {
      try {
        const response = await axios.get(`${API}/admin/refresh-status`, {
          headers: getAuthHeader()
        });
        setRefreshStatus(response.data);
        
        const mediaRunning = response.data.media_job?.is_running;
        const persRunning = response.data.personalities_job?.is_running;
        
        if (mediaRunning || persRunning) {
          // Still running, poll again in 3 seconds
          setTimeout(poll, 3000);
        } else {
          // Both complete
          setBgRefreshing(false);
          const mediaCount = response.data.media_job?.articles_added || 0;
          const persCount = response.data.personalities_job?.articles_added || 0;
          const persNames = response.data.personalities_job?.personalities_found?.length || 0;
          toast.success(`Refresh complete! ${mediaCount} media articles + ${persCount} personality articles (${persNames} people)`);
          fetchSettings();
        }
      } catch (error) {
        console.error('Error polling status:', error);
        setBgRefreshing(false);
      }
    };
    poll();
  };

  return (
    <div className="min-h-screen bg-[#FFFDF5]">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-12">
          <h1 className="text-5xl md:text-7xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
            Admin Dashboard
          </h1>
          <p className="text-xl text-gray-600">
            Manage your daily wit
          </p>
          <Link
            to="/analytics"
            data-testid="analytics-link"
            className="inline-flex items-center gap-2 mt-4 px-6 py-3 bg-[#4ECDC4] text-black font-bold rounded-full border-2 border-black hard-shadow-hover"
          >
            <BarChart3 className="w-5 h-5" strokeWidth={2.5} />
            View Analytics
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white border-2 border-black rounded-xl p-8 hard-shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#FF6B6B] p-2 rounded-lg border-2 border-black">
                <RefreshCw className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <h2 className="text-3xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
                Refresh News
              </h2>
            </div>

            <p className="text-gray-600 mb-6">
              Fetch the latest news and generate humorous summaries
            </p>

            {lastRefresh && (
              <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 text-green-500" strokeWidth={2.5} />
                <span>Last refresh: {new Date(lastRefresh).toLocaleString()}</span>
              </div>
            )}

            {/* NEW: Background Refresh All Button */}
            <button
              onClick={handleRefreshAllBackground}
              disabled={bgRefreshing}
              data-testid="refresh-all-background-button"
              className="w-full bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] text-white font-bold py-4 px-6 rounded-full border-2 border-black hard-shadow-hover disabled:opacity-50 flex items-center justify-center gap-2 mb-3"
            >
              {bgRefreshing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.5} />
                  Refreshing in Background...
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5" strokeWidth={2.5} />
                  🚀 Refresh All (Media + Personalities)
                </>
              )}
            </button>

            {/* Background Refresh Status */}
            {refreshStatus && bgRefreshing && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
                <div className="font-semibold mb-2">Background Refresh Status:</div>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>📰 Media:</span>
                    <span className={refreshStatus.media_job?.is_running ? 'text-blue-600' : 'text-green-600'}>
                      {refreshStatus.media_job?.is_running ? `${refreshStatus.media_job?.current_category}...` : '✓ Done'} 
                      ({refreshStatus.media_job?.articles_added || 0} articles)
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>👤 Personalities:</span>
                    <span className={refreshStatus.personalities_job?.is_running ? 'text-blue-600' : 'text-green-600'}>
                      {refreshStatus.personalities_job?.is_running ? 'Searching...' : '✓ Done'}
                      ({refreshStatus.personalities_job?.articles_added || 0} articles, {refreshStatus.personalities_job?.personalities_found?.length || 0} people)
                    </span>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-news-button"
              className="w-full bg-[#FF6B6B] text-white font-bold py-3 px-6 rounded-full border-2 border-black hard-shadow-hover disabled:opacity-50 flex items-center justify-center gap-2 mb-3"
            >
              {refreshing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.5} />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" strokeWidth={2.5} />
                  Refresh by Category
                </>
              )}
            </button>

            <button
              onClick={handleClearAllNews}
              disabled={clearLoading}
              data-testid="clear-news-button"
              className="w-full bg-gray-800 text-white font-bold py-3 px-6 rounded-full border-2 border-black hard-shadow-hover disabled:opacity-50 flex items-center justify-center gap-2 mb-3"
            >
              {clearLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.5} />
                  Clearing...
                </>
              ) : (
                <>
                  Clear All News (Start Fresh)
                </>
              )}
            </button>

            <button
              onClick={handleRefreshByTrends}
              disabled={trendingLoading}
              data-testid="refresh-trending-button"
              className="w-full bg-[#4ECDC4] text-black font-bold py-3 px-6 rounded-full border-2 border-black hard-shadow-hover disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {trendingLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.5} />
                  Fetching Trends...
                </>
              ) : (
                <>
                  <TrendingUp className="w-5 h-5" strokeWidth={2.5} />
                  Refresh by Trending Topics 🔥
                </>
              )}
            </button>
          </div>

          <div className="bg-white border-2 border-black rounded-xl p-8 hard-shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#4ECDC4] p-2 rounded-lg border-2 border-black">
                <Settings className="w-6 h-6 text-black" strokeWidth={2.5} />
              </div>
              <h2 className="text-3xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
                Humor Level
              </h2>
            </div>

            <p className="text-gray-600 mb-6">
              Adjust how spicy the news summaries should be
            </p>

            <div className="space-y-3">
              {['low', 'medium', 'spicy'].map((level) => (
                <button
                  key={level}
                  onClick={() => handleHumorLevelChange(level)}
                  disabled={loading}
                  data-testid={`humor-level-${level}`}
                  className={`w-full py-3 px-6 rounded-full border-2 border-black font-bold uppercase tracking-wider transition-all ${
                    humorLevel === level
                      ? 'bg-[#FFE66D] text-black hard-shadow'
                      : 'bg-white text-black hover:bg-[#F0F0F0] hard-shadow-hover'
                  } disabled:opacity-50`}
                >
                  {level === 'low' && '😊 Low'}
                  {level === 'medium' && '😄 Medium'}
                  {level === 'spicy' && '🌶️ Spicy'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Photo of the Week Section */}
        <div className="mt-8 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] border-2 border-black rounded-xl p-6 hard-shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <Camera className="w-6 h-6 text-white" strokeWidth={2.5} />
            <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Merriweather, serif' }}>
              Photo of the Week 📸
            </h3>
          </div>
          <p className="text-white/90 mb-4">
            Generate a featured photo with a witty caption from the week's top story. Perfect for Fridays!
          </p>
          <button
            onClick={handleGeneratePhoto}
            disabled={photoLoading}
            data-testid="generate-photo-btn"
            className="bg-white text-black font-bold px-6 py-3 rounded-full border-2 border-black hard-shadow-hover disabled:opacity-50 flex items-center gap-2"
          >
            {photoLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.5} />
                Generating...
              </>
            ) : (
              <>
                <Camera className="w-5 h-5" strokeWidth={2.5} />
                Generate Photo of the Week
              </>
            )}
          </button>
        </div>

        {/* Newsletter Section */}
        <div className="mt-8 bg-gradient-to-r from-[#4ECDC4] to-[#44A08D] border-2 border-black rounded-xl p-6 hard-shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-6 h-6 text-white" strokeWidth={2.5} />
            <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Merriweather, serif' }}>
              Send Newsletter
            </h3>
          </div>
          <p className="text-white/90 mb-4">
            Send today's top stories to all newsletter subscribers.
          </p>
          <button
            onClick={handleSendNewsletter}
            disabled={newsletterLoading}
            data-testid="send-newsletter-btn"
            className="bg-white text-black font-bold px-6 py-3 rounded-full border-2 border-black hard-shadow-hover disabled:opacity-50 flex items-center gap-2"
          >
            {newsletterLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.5} />
                Sending...
              </>
            ) : (
              <>
                <Mail className="w-5 h-5" strokeWidth={2.5} />
                Send Newsletter Now
              </>
            )}
          </button>
        </div>

        <div className="mt-8 bg-[#FFE66D] border-2 border-black rounded-xl p-6 hard-shadow">
          <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
            💡 Pro Tip
          </h3>
          <p className="text-gray-700">
            For best results, refresh news once daily in the morning. The humor level affects all future news generation.
          </p>
        </div>

        {trendingTopics.length > 0 && (
          <div className="mt-8 bg-white border-2 border-black rounded-xl p-6 hard-shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="w-6 h-6 text-[#FF6B6B]" strokeWidth={2.5} />
              <h3 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
                What's Trending Right Now 🔥
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">Top searches on Google today:</p>
            <div className="flex flex-wrap gap-2">
              {trendingTopics.slice(0, 15).map((topic, index) => (
                <span
                  key={index}
                  className="bg-[#F0F0F0] border-2 border-black rounded-full px-4 py-2 text-sm font-bold hover:bg-[#FFE66D] transition-all cursor-default"
                >
                  {topic}
                </span>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Click "Refresh by Trending Topics" above to fetch news about these hot topics!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;